// src/components/ws/WsSplit.jsx
// 出席記録（全画面）の「表＋横の欄」の枠【2026-10-13】
//
//   出欠表・回ごとの横の欄を、使う人が動かせるようにする。
//     ・境目の線をドラッグすると、欄の幅が変わる（ダブルクリックで元の幅）
//       線にフォーカスして ← → でも変えられる
//     ・欄の上の「⇄」で左右を入れ替える
//     ・「畳む」で欄をしまい、表を広く使う。名前やマスを押すと、また開く（openSignal）
//   幅・左右・畳んだかどうかは、このブラウザに覚える（タブごと）。
//   画面が狭いとき（900px 以下）は欄を表の下に積むので、幅の調整は出さない（App.css）。
//
//   使い方:
//     <WsSplit id="matrix" label="選んだ学生" openSignal={studentId}>
//       <div className="ws-split-main">…表…</div>
//       <aside className="ws-split-side">…欄…</aside>
//     </WsSplit>
import React, { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_W = 340;
const MIN_W = 240;

const read = (k, fb) => { try { const v = localStorage.getItem(k); return v == null ? fb : v; } catch (e) { return fb; } };
const write = (k, v) => { try { localStorage.setItem(k, String(v)); } catch (e) { /* 覚えられなくても動く */ } };

// flow … 枠の高さを画面に合わせず、ページと一緒にスクロールさせる（概要タブ）
export default function WsSplit({ id, label = '横の欄', openSignal, flow = false, children }) {
  const key = 'wsSplit.' + id;
  const [width, setWidth] = useState(() => {
    const v = parseInt(read(key + '.w', ''), 10);
    return v >= MIN_W ? v : DEFAULT_W;
  });
  const [side, setSide] = useState(() => (read(key + '.side', 'right') === 'left' ? 'left' : 'right'));
  const [collapsed, setCollapsed] = useState(() => read(key + '.collapsed', '0') === '1');
  const [dragging, setDragging] = useState(false);
  const boxRef = useRef(null);

  const maxW = () => {
    const box = boxRef.current;
    return box ? Math.max(MIN_W, Math.min(900, box.getBoundingClientRect().width - 360)) : 900;
  };
  const clamp = useCallback((w) => Math.round(Math.max(MIN_W, Math.min(maxW(), w))), []); // eslint-disable-line react-hooks/exhaustive-deps
  const saveWidth = (w) => { setWidth(w); write(key + '.w', w); };

  // 名前やマスを押したら、畳んでいても開く
  const firstSignal = useRef(true);
  useEffect(() => {
    if (firstSignal.current) { firstSignal.current = false; return; }
    if (openSignal != null && openSignal !== '' && collapsed) { setCollapsed(false); write(key + '.collapsed', '0'); }
  }, [openSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const box = boxRef.current;
    if (!box) return;
    setDragging(true);
    const rect = box.getBoundingClientRect();
    let last = width;
    const move = (ev) => {
      const w = side === 'right' ? rect.right - ev.clientX : ev.clientX - rect.left;
      last = clamp(w);
      setWidth(last);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      setDragging(false);
      write(key + '.w', last);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  const onKeyDown = (e) => {
    const step = e.shiftKey ? 60 : 20;
    // 欄が右にあるときは ← で広がる、左にあるときは → で広がる
    const grow = side === 'right' ? 'ArrowLeft' : 'ArrowRight';
    const shrink = side === 'right' ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === grow) { e.preventDefault(); saveWidth(clamp(width + step)); }
    else if (e.key === shrink) { e.preventDefault(); saveWidth(clamp(width - step)); }
    else if (e.key === 'Home') { e.preventDefault(); saveWidth(DEFAULT_W); }
  };

  // 画面の幅が変わって、欄が広すぎるようになったら縮める（覚えた値はそのまま）
  const [, force] = useState(0);
  useEffect(() => {
    const onResize = () => force(n => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const flip = () => { const v = side === 'right' ? 'left' : 'right'; setSide(v); write(key + '.side', v); };
  const fold = (v) => { setCollapsed(v); write(key + '.collapsed', v ? '1' : '0'); };

  const [main, sideEl] = React.Children.toArray(children);
  const shown = boxRef.current ? clamp(width) : width;

  const tools = (
    <div className="ws-side-tools">
      <span className="ws-side-tools-label">{label}</span>
      <button type="button" className="ws-side-tool" onClick={flip}
              title={side === 'right' ? '欄を左へ動かす' : '欄を右へ動かす'} aria-label={side === 'right' ? '欄を左へ動かす' : '欄を右へ動かす'}>
        {side === 'right' ? '⇠ 左へ' : '右へ ⇢'}
      </button>
      <button type="button" className="ws-side-tool" onClick={() => fold(true)} title="欄を畳んで、表を広く使う" aria-label="欄を畳む">
        畳む
      </button>
    </div>
  );

  return (
    <div ref={boxRef}
         className={'ws-split' + (flow ? ' ws-split-flow' : '') + (side === 'left' ? ' is-left' : '') + (dragging ? ' is-dragging' : '') + (collapsed ? ' is-collapsed' : '')}
         style={{ '--side-w': shown + 'px' }}>
      {main}
      {collapsed ? (
        <button type="button" className="ws-side-reopen" onClick={() => fold(false)} title={label + 'を開く'}>
          <span>{side === 'right' ? '◂' : '▸'}</span>
          <span className="ws-side-reopen-label">{label}</span>
        </button>
      ) : (
        <>
          <div className="ws-splitter" role="separator" aria-orientation="vertical" tabIndex={0}
               aria-label={label + 'の幅（ドラッグ、または ← → で変える。ダブルクリックで元に戻す）'}
               aria-valuenow={shown} aria-valuemin={MIN_W} aria-valuemax={900}
               title="ドラッグで幅を変える（ダブルクリックで元の幅）"
               onPointerDown={onPointerDown} onMouseDown={(e) => e.preventDefault()} onKeyDown={onKeyDown} onDoubleClick={() => saveWidth(DEFAULT_W)}>
            <span className="ws-splitter-grip" aria-hidden="true" />
          </div>
          {sideEl && React.cloneElement(sideEl, {}, tools, sideEl.props.children)}
        </>
      )}
    </div>
  );
}
